/**
 * Source-linked build specifications for the three warm-season Atlas models
 * that need diagnostic forms beyond a generic grass plate. These are model
 * inputs, not an assertion that a rendered asset is botanically approved.
 */

export type DistinctWarmModelId = 'kikuyugrass' | 'bahiagrass' | 'buffalograss'

type EvidenceLevel = 'direct' | 'seasonal-or-cultivar-variable'

export interface DistinctWarmModelSource {
  id: string
  publisher: string
  title: string
  href: string
}

export interface DistinctWarmModelFeature {
  id: string
  label: string
  /** What a future procedural renderer must make visible. */
  renderInstruction: string
  /** Why the feature earns space in an identification model. */
  diagnosticPurpose: string
  sourceIds: readonly string[]
  evidence: EvidenceLevel
}

export interface DistinctWarmModelSpec {
  id: DistinctWarmModelId
  commonName: string
  scientificName: string
  status: 'specification-only'
  reviewLabel: string
  reviewNote: string
  sourceRecords: readonly DistinctWarmModelSource[]
  mandatoryFeatures: readonly DistinctWarmModelFeature[]
  /**
   * These constraints intentionally prevent a renderer from filling unknown
   * morphology with plausible-looking but unsupported detail.
   */
  uncertaintyAndReview: readonly string[]
}

export const DISTINCT_WARM_MODEL_SPECS: Record<DistinctWarmModelId, DistinctWarmModelSpec> = {
  kikuyugrass: {
    id: 'kikuyugrass',
    commonName: 'Kikuyugrass',
    scientificName: 'Cenchrus clandestinus',
    status: 'specification-only',
    reviewLabel: '3D teaching model specification',
    reviewNote: 'The geometry follows leaf-base, stolon, and flowering reference photos.',
    sourceRecords: [
      {
        id: 'uc-ipm-kikuyu-gallery',
        publisher: 'UC Statewide IPM Program',
        title: 'Kikuyugrass weeds identification gallery',
        href: 'https://ipm.ucanr.edu/weeds-identification-gallery/kikuyugrass/',
      },
      {
        id: 'uc-ipm-kikuyu-pest-notes',
        publisher: 'UC Statewide IPM Program',
        title: 'Kikuyugrass Pest Notes',
        href: 'https://ipm.ucanr.edu/pdf/pestnotes/pnkikuyugrass.pdf',
      },
    ],
    mandatoryFeatures: [
      {
        id: 'flattened-stolons',
        label: 'Stout, slightly flattened stolons',
        renderInstruction: 'Use thick, coarse horizontal surface stems with subtle flattening, swollen nodes, brown sheaths below nodes, and roots emerging where nodes contact soil. Build a dense surface mat, not isolated runners.',
        diagnosticPurpose: 'Separates the aggressive Kikuyu mat from a bunch grass and from finer stolon-formers.',
        sourceIds: ['uc-ipm-kikuyu-gallery', 'uc-ipm-kikuyu-pest-notes'],
        evidence: 'direct',
      },
      {
        id: 'thick-rhizomes',
        label: 'Thick underground rhizomes',
        renderInstruction: 'Below the soil plane, continue the creeping-stem network as thick rhizomes 1 to 4 inches deep, visibly distinct from fine fibrous roots.',
        diagnosticPurpose: 'Makes the dual stolon-and-rhizome spread habit inspectable rather than implying stolons alone.',
        sourceIds: ['uc-ipm-kikuyu-pest-notes'],
        evidence: 'direct',
      },
      {
        id: 'hairy-collar',
        label: 'Long unequal hair fringe at the pale collar',
        renderInstruction: 'Add a clearly enlarged, pale collar with a long, uneven fringe of fine hairs running parallel to the stem. Keep auricles absent.',
        diagnosticPurpose: 'The collar hair fringe is a high-value close-up confirmation cue.',
        sourceIds: ['uc-ipm-kikuyu-gallery', 'uc-ipm-kikuyu-pest-notes'],
        evidence: 'direct',
      },
      {
        id: 'hairy-pointed-blades',
        label: 'Light-green, flat, pointed, slightly hairy blades',
        renderInstruction: 'Use flat blades with pointed tips, a light-green coarse appearance, and sparse short hairs on the blade and sheath. Avoid the blunt, sharply creased look of St. Augustinegrass.',
        diagnosticPurpose: 'Supports the visible contrast with St. Augustinegrass, a common lookalike.',
        sourceIds: ['uc-ipm-kikuyu-gallery', 'uc-ipm-kikuyu-pest-notes'],
        evidence: 'direct',
      },
      {
        id: 'concealed-flower-structure',
        label: 'Concealed flower structure with occasional white anthers',
        renderInstruction: 'Do not add a conventional exposed terminal seedhead. Provide an optional cool, humid morning state in which long white filaments with protruding anthers rise just above leaves, while the female structure remains low and seeds stay inside terminal leaf sheaths.',
        diagnosticPurpose: 'Prevents a misleading generic grass seedhead and preserves Kikuyu’s unusual flowering evidence.',
        sourceIds: ['uc-ipm-kikuyu-gallery', 'uc-ipm-kikuyu-pest-notes'],
        evidence: 'seasonal-or-cultivar-variable',
      },
    ],
    uncertaintyAndReview: [
      'Blade width, hair density, and stolon flattening vary with mowing, maturity, and growing conditions. Use diagnostic exaggeration only after reference-photo review.',
      'White anthers are most obvious under cool, humid morning conditions. They must be an optional flowering state, not a permanent canopy feature.',
      'Flowers stay inside the terminal leaf sheath, so the model does not show a raised seedhead.',
    ],
  },
  bahiagrass: {
    id: 'bahiagrass',
    commonName: 'Bahiagrass',
    scientificName: 'Paspalum notatum',
    status: 'specification-only',
    reviewLabel: '3D teaching model specification',
    reviewNote: 'The crown, rhizome placement, collar, and flowering raceme follow region and cultivar references.',
    sourceRecords: [
      {
        id: 'uf-ifas-turf-id',
        publisher: 'University of Florida IFAS Extension',
        title: 'Florida Turfgrass Identification',
        href: 'https://ask.ifas.ufl.edu/publication/EP646',
      },
      {
        id: 'nc-state-turffiles-bahia',
        publisher: 'NC State Extension',
        title: 'Bahiagrass TurfFiles',
        href: 'https://www.turffiles.ncsu.edu/grasses/bahiagrass/',
      },
      {
        id: 'texas-am-bahia',
        publisher: 'Texas A&M AgriLife Extension',
        title: 'Bahiagrass',
        href: 'https://aggieturf.tamu.edu/turfgrass-weeds/bahiagrass/',
      },
    ],
    mandatoryFeatures: [
      {
        id: 'coarse-open-crown',
        label: 'Coarse, open crown with crowded basal leaves',
        renderInstruction: 'Build a coarse, comparatively open canopy from a crowded basal crown. Keep leaf blades medium-to-coarse and pointed, rather than creating a fine dense carpet.',
        diagnosticPurpose: 'Communicates the low, open bahiagrass habit that contrasts with dense high-quality turf.',
        sourceIds: ['uf-ifas-turf-id', 'nc-state-turffiles-bahia'],
        evidence: 'direct',
      },
      {
        id: 'stout-rhizomes',
        label: 'Stout, woody-to-fleshy rhizomes',
        renderInstruction: 'Show thick shallow underground rhizomes radiating from the crown, with a restrained purple tint near the base. They may approach the surface but must remain visibly different from true aboveground stolons.',
        diagnosticPurpose: 'Makes bahiagrass’s aggressive rhizome spread visible without falsely classifying it as a stolon grass.',
        sourceIds: ['uf-ifas-turf-id', 'nc-state-turffiles-bahia', 'texas-am-bahia'],
        evidence: 'direct',
      },
      {
        id: 'hair-fringe-ligule',
        label: 'Membranous ligule with a hair fringe',
        renderInstruction: 'Use a divided collar, open flattened sheath, and small membranous ligule with a fringe of hairs. Auricles must be absent.',
        diagnosticPurpose: 'Provides an up-close confirmation cue when the seedhead is unavailable.',
        sourceIds: ['uf-ifas-turf-id', 'nc-state-turffiles-bahia'],
        evidence: 'direct',
      },
      {
        id: 'y-shaped-raceme',
        label: 'Two-branched Y or V seedhead',
        renderInstruction: 'Make the flowering stalk conspicuous: two racemes should fork sharply at the top into a Y or V. Allow an optional third branch and visible purple anthers, but preserve the two-branch form as the primary teaching view.',
        diagnosticPurpose: 'This is the species’s most immediately recognizable field cue.',
        sourceIds: ['uf-ifas-turf-id', 'nc-state-turffiles-bahia', 'texas-am-bahia'],
        evidence: 'direct',
      },
      {
        id: 'rolled-vernation',
        label: 'Rolled new leaf, sometimes appearing folded',
        renderInstruction: 'Make the developing shoot visibly rolled, while allowing the renderer to show an ambiguous near-folded appearance in mature or compressed leaves.',
        diagnosticPurpose: 'Keeps the model aligned with real field ambiguity instead of teaching a brittle single-view cue.',
        sourceIds: ['uf-ifas-turf-id', 'nc-state-turffiles-bahia'],
        evidence: 'seasonal-or-cultivar-variable',
      },
    ],
    uncertaintyAndReview: [
      'Do not render true aboveground stolons. Stout rhizomes may appear near the soil surface and can be mistaken for stolons in field photos.',
      'Cultivars vary in leaf width, posture, and rhizome expression. The model should teach species-level traits, not a single named cultivar.',
      'The two-branched seedhead is the primary cue, but a third branch and purple anthers can also occur.',
    ],
  },
  buffalograss: {
    id: 'buffalograss',
    commonName: 'Buffalograss',
    scientificName: 'Bouteloua dactyloides',
    status: 'specification-only',
    reviewLabel: '3D teaching model specification',
    reviewNote: 'Male and female flower states remain separate, with hair placement, blade posture, and flowering behavior based on the cited references.',
    sourceRecords: [
      {
        id: 'okstate-buffalo-profile',
        publisher: 'Oklahoma State University Extension',
        title: 'Buffalograss plant identification profile',
        href: 'https://extension.okstate.edu/programs/plant-id/plant-profiles/buffalograss',
      },
      {
        id: 'okstate-turfgrass-curriculum',
        publisher: 'Oklahoma State University Extension',
        title: 'Turfgrass: A Middle School Curriculum',
        href: 'https://extension.okstate.edu/fact-sheets/turfgrass-a-middle-school-curriculum',
      },
      {
        id: 'colorado-state-buffalo-lawns',
        publisher: 'Colorado State University Extension',
        title: 'Buffalograss Lawns',
        href: 'https://extension.colostate.edu/resource/buffalograss-lawns/',
      },
    ],
    mandatoryFeatures: [
      {
        id: 'low-hairy-rolled-blades',
        label: 'Short, gray-green, hairy blades with a rolled young-leaf state',
        renderInstruction: 'Keep the turf low and short, with gray-green blades carrying visible small hairs on both surfaces. Use modest longitudinal rolling and a few relaxed curled blade tips to express the rolled vernation without turning every blade into a coil.',
        diagnosticPurpose: 'Separates the soft, hairy, short Buffalo canopy from wiry Bermudagrass and from bunch-forming blue grama.',
        sourceIds: ['okstate-buffalo-profile', 'okstate-turfgrass-curriculum'],
        evidence: 'direct',
      },
      {
        id: 'hairy-collar-ligule',
        label: 'Broad hairy collar and ciliate ligule',
        renderInstruction: 'Create a broad continuous collar with long hairs and a fringe-of-hairs ligule. Leave auricles absent; the sheath should remain open.',
        diagnosticPurpose: 'Lets learners inspect a high-value vegetative cue before flowers appear.',
        sourceIds: ['okstate-turfgrass-curriculum', 'okstate-buffalo-profile'],
        evidence: 'direct',
      },
      {
        id: 'wiry-rooting-stolons',
        label: 'Naked, wiry stolons that root at contact points',
        renderInstruction: 'Use slender exposed stolons running over the soil and rooting at contact points to form new plants. Do not add rhizomes as a defining Buffalo feature.',
        diagnosticPurpose: 'Shows its sod-forming spread while distinguishing it from species with dual runners.',
        sourceIds: ['okstate-buffalo-profile', 'colorado-state-buffalo-lawns'],
        evidence: 'direct',
      },
      {
        id: 'male-flag-flowers',
        label: 'Male flag-like flower state',
        renderInstruction: 'Provide a dedicated male plant state with one to three small spike branches held above the canopy like flags. Do not combine it with female burs on the same individual.',
        diagnosticPurpose: 'Captures the conspicuous flower form of this dioecious species.',
        sourceIds: ['okstate-buffalo-profile'],
        evidence: 'direct',
      },
      {
        id: 'female-bur-flowers',
        label: 'Female bur flower state hidden among blades',
        renderInstruction: 'Provide a distinct female plant state with small bur-like flower structures tucked within the leaves near the canopy. Keep them lower and less conspicuous than the male flags.',
        diagnosticPurpose: 'Prevents the common error of treating every Buffalograss flower as an upright seedhead.',
        sourceIds: ['okstate-buffalo-profile', 'okstate-turfgrass-curriculum'],
        evidence: 'direct',
      },
    ],
    uncertaintyAndReview: [
      'Buffalograss is dioecious. A single modeled plant must show male flowers or female burs, never both.',
      'Vegetatively propagated turf cultivars can be female-only, and mowing can remove floral evidence. Flower states need to be optional.',
      'The sources confirm rolled vernation and hairy surfaces. Mature-blade curl stays modest because it varies across real plants.',
    ],
  },
}
